import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getQueryKey, LogsType } from '@/hooks/api/stepExecutions/useRetrieveStepExecutionLogs.ts';
import { isQueryMountedWithData } from '@/hooks/api/sse/utils.ts';
import { StepExecutionLogEntry, StepExecutionLogs } from '@/types/stepExecutions.ts';

const useInvalidateStepExecutionLogs = (automationId: string, logsType: LogsType) => {
  const queryClient = useQueryClient();

  // for normal logs parentId is the stepExecutionId, for extract_entity_errors it's the extractEntityId
  return useCallback(
    (newEntry: StepExecutionLogEntry, parentId: string): unknown[][] => {
      const key = getQueryKey(automationId, parentId, logsType);

      if (!isQueryMountedWithData(queryClient, key)) {
        return [key];
      }

      queryClient.setQueryData<StepExecutionLogs>(key, (old) => {
        const currentEntries = old ?? [];

        const entryExists = currentEntries.some((entry) => entry.id === newEntry.id);
        const nextEntries = entryExists
          ? currentEntries.map((entry) => (entry.id === newEntry.id ? newEntry : entry))
          : [...currentEntries, newEntry];

        return nextEntries.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
      });

      return [];
    },
    [automationId, logsType, queryClient],
  );
};

export default useInvalidateStepExecutionLogs;
